import { useNavigate, Link } from "react-router-dom";
import { Trophy, Users, Zap, ArrowRight, Sparkles, KeyRound, Mail, X, Lock, XCircle, ChevronDown, LogIn, UserPlus, Flame } from "lucide-react";
import TermsModal from "@/components/TermsModal";
import PrivacyModal from "@/components/PrivacyModal";
import ProPlusBillingTermsModal from "@/components/ProPlusBillingTermsModal";
import FirstTimeConsentModal from "@/components/FirstTimeConsentModal";
import { hasPlatformConsent, recordPlatformConsent } from "@/lib/platformConsent";
import HowItWorksCarousel from "@/components/landing/HowItWorksCarousel";
import { useEffect, useState } from "react";
import { api, session } from "@/lib/api";
import { ensurePaddleInitialized } from "@/lib/paddleClient";
import { toast } from "sonner";

export default function Landing() {
    const navigate = useNavigate();
    const [mode, setMode] = useState("join"); // 'join' | 'recover' — Request flow moved to /register (iter71)
    const [loading, setLoading] = useState(false);
    // teams_per_participant moved to TournamentSetup.jsx (iter31n) — admins
    // pick this AFTER they know what tournament they're running. Default 2 is
    // applied server-side until they reach the setup screen.
    const [inviteCode, setInviteCode] = useState("");
    const [username, setUsername] = useState("");
    // iter87.97 — joinEmail state removed; join is strict-invite-only.
    // Operator supplies the participant's email at invite time.
    const [joinPersonalCode, setJoinPersonalCode] = useState("");
    const [issuedCode, setIssuedCode] = useState(null); // {code, league_name} shown in modal after join
    const [recoverCode, setRecoverCode] = useState("");
    const [recoverEmail, setRecoverEmail] = useState("");
    const [recoverSent, setRecoverSent] = useState(false);
    const [existing, setExisting] = useState(null);
    // iter31m — site-wide flag: when false, we show "we're not accepting requests"
    // copy instead of (or alongside) the form. Defaults to true so a slow API
    // response doesn't accidentally block the form.
    const [requestsOpen, setRequestsOpen] = useState(true);
    // iter31n — T&C acceptance state + modal control
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
    const [proPlusTermsModalOpen, setProPlusTermsModalOpen] = useState(false);
    // iter87.133 — First-time crewmate consent modal state. Backend returns
    // 400 `{code: "consent_required"}` when a placeholder-claim POST arrives
    // without both flags; that opens this modal. Accepting re-runs the
    // same POST with `accepted_tc: true, age_confirmed: true`; declining
    // surfaces the refusal card below the join card.
    const [consentOpen, setConsentOpen] = useState(false);
    const [consentDeclined, setConsentDeclined] = useState(false);
    const [consentSubmitting, setConsentSubmitting] = useState(false);
    // iter87.180.14 — Top-ribbon "Access Your Rivalry" dropdown +
    // login modal. Sign Up routes to the rivalry-registration flow;
    // Login jumps straight to the crewmate's league if a session
    // cookie is present, otherwise opens the invite / personal code /
    // username modal.
    const [accessMenuOpen, setAccessMenuOpen] = useState(false);
    const [loginModalOpen, setLoginModalOpen] = useState(false);

    // iter87.199.155 — Paddle Retain's "Configure paddle.js" setup
    // step requires an unauthenticated Paddle.Initialize() call on a
    // public page that doesn't redirect, use query params, or gate on
    // login — this homepage (`/`) qualifies, checkout pages don't. No
    // checkout UI is opened here; this purely satisfies Retain's
    // installation check and lets Retain's one-click recovery links
    // work if/when a customer clicks one from a dunning email.
    useEffect(() => {
        let cancelled = false;
        let cfgLoaded = null;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/paddle/public-config`)
            .then((r) => (r.ok ? r.json() : null))
            .then((cfg) => { cfgLoaded = cfg; })
            .catch(() => {}); // Best-effort — a logged-out homepage visitor never needs this to work.
        // `paddle.js` loads with `defer` — poll briefly for `window.Paddle`
        // to exist rather than racing the script tag on first paint.
        let tries = 0;
        const tick = setInterval(() => {
            tries += 1;
            if (cancelled) { clearInterval(tick); return; }
            if (cfgLoaded?.client_token && window.Paddle) {
                clearInterval(tick);
                ensurePaddleInitialized(cfgLoaded.client_token, cfgLoaded.environment).catch(() => {});
            } else if (tries > 25) {
                clearInterval(tick); // ~5s — give up quietly, nothing user-facing depends on this.
            }
        }, 200);
        return () => { cancelled = true; clearInterval(tick); };
    }, []);

    useEffect(() => {
        const s = session.load();
        if (s) setExisting(s);
        // Best-effort fetch — if it fails (e.g. offline), keep requestsOpen=true.
        api.getSiteSettings()
            .then((r) => setRequestsOpen(r?.league_requests_open !== false))
            .catch(() => {});
        // iter74 — Prefill invite + personal code from URL query string so
        // an admin-shared "claim link" (e.g.
        //   https://.../?code=ABC123&pcode=XYZ-1234)
        // drops the user straight into the Join form with both fields ready.
        // The user still has to pick their username and submit — the link is
        // a convenience, not an auto-claim.
        try {
            const params = new URLSearchParams(window.location.search);
            const c = (params.get("code") || "").trim().toUpperCase();
            const pc = (params.get("pcode") || "").trim().toUpperCase();
            if (c) setInviteCode(c);
            if (pc) setJoinPersonalCode(pc);
        } catch (_e) { /* ignore */ }
    }, []);

    const handleJoin = async (e, opts = {}) => {
        e?.preventDefault?.();
        if (!inviteCode.trim() || !username.trim()) {
            toast.error("Enter invite code and your username");
            return;
        }
        // iter87.133 — On the FIRST submit we send `false/false` so the
        // backend gate fires for first-time crewmates. When the modal
        // accept fires this handler again with `{acceptedTc: true,
        // ageConfirmed: true}`, we re-send with the flags on.
        // iter87.199.99 — If the browser already has a stored platform
        // consent (this user consented earlier in a different flow), we
        // skip the modal by auto-flagging both fields on the FIRST call
        // so the backend gate never trips. Per-league audit stamp
        // (iter87.199.69) still records the acceptance.
        const priorConsent = hasPlatformConsent();
        const acceptedTc = !!opts.acceptedTc || priorConsent;
        const ageConfirmed = !!opts.ageConfirmed || priorConsent;
        setLoading(true);
        try {
            const data = await api.joinLeague({
                invite_code: inviteCode.trim().toUpperCase(),
                username: username.trim(),
                email: undefined,
                personal_code: joinPersonalCode.trim() || undefined,
                accepted_tc: acceptedTc,
                age_confirmed: ageConfirmed,
            });
            session.save(data);
            setConsentOpen(false);
            toast.success(data.rejoined ? "Welcome back" : `Joined "${data.league.name}"`);
            // iter35c — late_join is now always False from the backend (post-draft
            // joins are hard-blocked with a 400). The toast below is preserved
            // only as a defensive no-op for any legacy backend response shape.
            if (data.late_join) {
                toast(
                    "Heads up — the draft already happened, so you don't have any teams yet. Ask the rivalry admin to redraft or assign you teams manually.",
                    { duration: 12000, icon: "⚠️" }
                );
            }
            // Show the personal code modal only if the server just issued
            // it (brand-new join OR legacy lazy upgrade). Already-coded
            // rejoins don't re-display it.
            if (data.personal_code_issued && data.participant?.personal_code) {
                setIssuedCode({
                    code: data.participant.personal_code,
                    league_name: data.league.name,
                    redirect_to: `/league/${data.league.id}`,
                });
                setLoading(false);
                return;
            }
            navigate(`/league/${data.league.id}`);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const detailCode = (typeof detail === "object" ? detail?.code : "") || "";
            // iter87.133 — First-time crewmate: pop the consent modal and
            // await accept/decline. Frontend does not surface an error
            // toast for this state — the modal IS the UX.
            if (err.response?.status === 400 && detailCode === "consent_required") {
                setConsentOpen(true);
                setConsentDeclined(false);
                setLoading(false);
                setConsentSubmitting(false);
                return;
            }
            // 401 with personal_code_required → toast guidance and reveal code field
            if (err.response?.status === 401 && (typeof detail === "object" ? detail?.code : detail || "").toString().includes("personal_code")) {
                toast.error("This username needs your personal code. Enter it below or use the recovery link.",
                    { duration: 8000 });
                // Highlight the field so the user sees it
                setJoinPersonalCode("");
                const el = document.querySelector('[data-testid="join-personal-code-input"]');
                if (el) el.focus();
            } else {
                toast.error((typeof detail === "string" ? detail : detail?.message) || "Failed to join");
            }
        }
        setLoading(false);
        setConsentSubmitting(false);
    };

    // iter87.133 — Consent modal accept → re-run handleJoin with flags on.
    // iter87.199.99 — Also persist to localStorage so future gates skip.
    const handleConsentAccept = async () => {
        setConsentSubmitting(true);
        recordPlatformConsent();
        await handleJoin(null, { acceptedTc: true, ageConfirmed: true });
    };

    // iter87.133 — Consent modal decline → close modal, show refusal card.
    const handleConsentDecline = () => {
        setConsentOpen(false);
        setConsentDeclined(true);
    };

    const handleRecover = async (e) => {
        e.preventDefault();
        if (!recoverCode.trim() || !recoverEmail.trim()) {
            toast.error("Enter invite code and your email");
            return;
        }
        setLoading(true);
        try {
            const data = await api.requestRecovery({
                invite_code: recoverCode.trim().toUpperCase(),
                email: recoverEmail.trim().toLowerCase(),
            });
            if (data.ok) {
                setRecoverSent(true);
                toast.success("Check your inbox");
            } else {
                toast.error(data.message || "Couldn't send recovery email — try again in a moment");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong");
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen relative overflow-hidden">
            {/* Background grain + accents */}
            <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-cyber/10 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-volt/10 blur-3xl rounded-full" />

            {/* Top bar */}
            <header className="relative z-20 flex items-center justify-between gap-2 px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                        <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-black" strokeWidth={2.5} />
                    </div>
                    <span className="font-display font-black text-base sm:text-xl tracking-tight truncate">TINY CREW RIVALRIES</span>
                </div>
                {/* iter87.180.14 — Access Your Rivalry ribbon dropdown */}
                {/* iter87.199.19 — Slimmer pill, volt-blue accent + pulsing
                    dot so first-time visitors immediately know where to
                    tap to get back into their rivalry. */}
                <div className="relative shrink-0">
                    <button
                        type="button"
                        data-testid="access-rivalry-btn"
                        onClick={() => setAccessMenuOpen((v) => !v)}
                        className="brutal-btn text-[11px] sm:text-xs py-1.5 px-2.5 sm:px-3 bg-cyber text-black border-2 border-cyber hover:bg-cyber/90"
                    >
                        <span className="relative flex h-2 w-2" data-testid="access-rivalry-pulse-dot">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
                        </span>
                        <span className="hidden sm:inline">Access Your Rivalry</span>
                        <span className="sm:hidden">Access</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${accessMenuOpen ? "rotate-180" : ""}`} />
                    </button>
                    {accessMenuOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-20"
                                onClick={() => setAccessMenuOpen(false)}
                                aria-hidden="true"
                            />
                            <div
                                className="absolute right-0 top-full mt-2 z-30 min-w-[15rem] brutal-card p-1.5 flex flex-col"
                                data-testid="access-rivalry-menu"
                                role="menu"
                            >
                                <button
                                    type="button"
                                    data-testid="access-menu-login-btn"
                                    onClick={() => {
                                        setAccessMenuOpen(false);
                                        if (existing) navigate(`/league/${existing.league_id}`);
                                        else setLoginModalOpen(true);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-white/5 rounded"
                                    role="menuitem"
                                >
                                    <LogIn className="w-4 h-4 text-cyber" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold">Login</div>
                                        <div className="text-[10px] text-zinc-500 leading-tight">
                                            {existing ? `Jump into ${existing.league_name}` : "Crewmate or league admin"}
                                        </div>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    data-testid="access-menu-signup-btn"
                                    onClick={() => {
                                        setAccessMenuOpen(false);
                                        navigate("/start");
                                    }}
                                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-white/5 rounded"
                                    role="menuitem"
                                >
                                    <UserPlus className="w-4 h-4 text-volt" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold">Sign Up</div>
                                        <div className="text-[10px] text-zinc-500 leading-tight">Start a new Rivalry for free</div>
                                    </div>
                                </button>
                                <div className="my-1 border-t border-white/10" aria-hidden="true" />
                                <a
                                    href="/rivalry-pro"
                                    data-testid="access-menu-pro-plus-link"
                                    onClick={() => setAccessMenuOpen(false)}
                                    className="px-3 py-1.5 text-[11px] text-zinc-500 hover:text-zinc-300 text-center"
                                    role="menuitem"
                                >
                                    Looking for Pro+?
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </header>

            {/* Hero — bottom padding tightened on mobile (iter87.199.59) so the
                "How it works" section peeks into view on scroll instead of
                sitting a full viewport away. Desktop keeps the generous rhythm. */}
            <main className="relative z-10 px-6 lg:px-12 pt-8 pb-8 lg:pb-20">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Left: copy */}
                    <div className="lg:col-span-12 space-y-8">
                        <div className="inline-flex items-center gap-2 pill bg-ink text-cyber border-cyber">
                            <Sparkles className="w-3.5 h-3.5" /> SMALL TEAMS. HUGE BRAGGING RIGHTS.
                        </div>
                        <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
                            DRAFT.<br />
                            <span className="text-cyber">CHEER.</span><br />
                            DOMINATE.
                        </h1>
                        <p className="text-lg lg:text-xl text-zinc-400 max-w-xl text-balance">
                            Built for small groups who take friendly rivalries way too seriously. Instant drafts, live scoring, and ruthless leaderboards—the ultimate excuse to keep your group chat alive.
                        </p>

                        {/* iter87.199.59 — 4 differentiator tiles in a 2×2 grid on
                            ≥sm; stacked on skinnier phone screens. Fourth tile is
                            "Everyone competes" — the direct antidote to big-pool
                            fatigue where nobody cares by week 8. Colour rotation
                            follows the app's palette so it feels intentional. */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl" data-testid="hero-features">
                            {[
                                { icon: Users, label: "Up to 20 crewmates", color: "text-cyber", testid: "hero-feature-crew"     },
                                { icon: Zap,   label: "60 Second Setup",     color: "text-volt",  testid: "hero-feature-setup"    },
                                { icon: Trophy,label: "Easy scoring",        color: "text-neon",  testid: "hero-feature-scoring"  },
                                { icon: Flame, label: "Everyone competes",   color: "text-blaze", testid: "hero-feature-competes" },
                            ].map((f) => (
                                <div
                                    key={f.label}
                                    data-testid={f.testid}
                                    className="brutal-card-light p-4 flex items-center gap-3"
                                >
                                    <f.icon className={`w-5 h-5 ${f.color}`} strokeWidth={2.5} />
                                    <span className="text-sm font-semibold">{f.label}</span>
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            </main>

            {/* iter87.199.58 — How-it-works carousel. Replaced the modal-only
                path (retired below) so first-time visitors see the app before
                they sign up. CTA jumps to the crew-registration flow. */}
            <HowItWorksCarousel onStartCta={() => navigate("/start")} />

            {/* Personal code reveal — shown once after a successful join when the
                server just issued the code. User must explicitly acknowledge before
                we navigate to the dashboard. */}
            {issuedCode && (
                <div
                    data-testid="personal-code-modal"
                    className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) { /* require explicit ack */ } }}
                >
                    <div className="brutal-card max-w-md w-full p-6 space-y-4 border-cyber/40">
                        <div className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-cyber" />
                            <h2 className="font-display font-extrabold text-2xl uppercase">Save your code</h2>
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed">
                            You're in for <strong className="text-cyber">{issuedCode.league_name}</strong>. Write this code down — you'll need it next time you log in from a new browser or device. <strong>It only shows up once.</strong>
                        </p>
                        <div className="bg-black border-2 border-cyber p-4 text-center">
                            <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-1">Personal code</p>
                            <p
                                data-testid="personal-code-value"
                                className="font-mono font-extrabold text-3xl text-volt tracking-[0.18em] select-all break-all"
                            >
                                {issuedCode.code}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                data-testid="personal-code-copy-btn"
                                onClick={() => {
                                    navigator.clipboard?.writeText(issuedCode.code);
                                    toast.success("Copied to clipboard");
                                }}
                                className="brutal-btn-ghost text-xs py-2 flex-1"
                            >
                                Copy
                            </button>
                            <button
                                data-testid="personal-code-continue-btn"
                                onClick={() => {
                                    const path = issuedCode.redirect_to;
                                    setIssuedCode(null);
                                    if (path) navigate(path);
                                }}
                                className="brutal-btn-primary text-sm py-2 flex-1"
                            >
                                I've saved it →
                            </button>
                        </div>
                        <p className="text-[11px] text-zinc-500 leading-relaxed">
                            Lost it later? Your rivalry admin can issue a new code, or use the recovery flow if you have an email on file.
                        </p>
                    </div>
                </div>
            )}

            {/* iter87.133 — Consent modal + refusal card. Modal is opened by
                handleJoin() when the backend replies `consent_required`.
                Refusal card is rendered as a fixed overlay so it feels
                as gate-y as the modal it replaces. */}
            <FirstTimeConsentModal
                open={consentOpen}
                leagueName={undefined}
                onAccept={handleConsentAccept}
                onDecline={handleConsentDecline}
                submitting={consentSubmitting}
            />
            {consentDeclined && (
                <div
                    data-testid="landing-consent-declined"
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                >
                    <div className="brutal-card max-w-md w-full p-6 space-y-4 text-center bg-ink">
                        <XCircle className="w-10 h-10 mx-auto text-heat" />
                        <h2 className="font-display font-black text-xl uppercase tracking-tight">
                            Can't enter the Rivalry
                        </h2>
                        <p className="text-sm text-zinc-400 leading-relaxed">
                            To join, you must be <strong className="text-white">18 or older</strong> and accept the{" "}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-cyber underline">Terms & Conditions</a>.
                            You can close this tab, or contact your rivalry admin if you have questions.
                        </p>
                        <button
                            type="button"
                            data-testid="landing-declined-retry-btn"
                            onClick={() => setConsentDeclined(false)}
                            className="brutal-btn inline-flex items-center gap-2 text-xs"
                        >
                            Change my mind
                        </button>
                    </div>
                </div>
            )}

            <footer className="relative z-10 border-t-2 border-black mt-auto py-6 px-6 lg:px-12 text-xs text-zinc-500 flex flex-col gap-3">
                <div className="flex flex-wrap gap-3 justify-between items-center">
                    <span>Built for tiny crews and big rivalries.</span>
                    <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
                        <button
                            type="button"
                            data-testid="footer-open-terms-btn"
                            onClick={() => setTermsModalOpen(true)}
                            className="text-zinc-400 hover:text-cyber underline-offset-2 hover:underline"
                        >
                            Terms of Use
                        </button>
                        <button
                            type="button"
                            data-testid="footer-open-privacy-btn"
                            onClick={() => setPrivacyModalOpen(true)}
                            className="text-zinc-400 hover:text-cyber underline-offset-2 hover:underline"
                        >
                            Privacy Notice
                        </button>
                        <button
                            type="button"
                            data-testid="footer-pro-plus-billing-terms-link"
                            onClick={() => setProPlusTermsModalOpen(true)}
                            className="text-zinc-400 hover:text-cyber underline-offset-2 hover:underline"
                        >
                            Pro+ Billing Terms
                        </button>
                        <span className="text-zinc-600">·</span>
                        <span>tinycrewrivalries.com</span>
                    </div>
                </div>
                {/* iter62d — Landing-page affiliation disclaimer (nominative fair use). */}
                <p
                    data-testid="footer-affiliation-disclaimer"
                    className="text-[10px] text-zinc-600 leading-relaxed"
                >
                    Not affiliated with FIFA, UEFA, the NCAA, the NFL, the PGA Tour, the DP World Tour, the IOC, or any other sports organization. Tournament names are descriptive labels only; all trademarks belong to their respective owners.
                </p>
            </footer>

            <TermsModal
                open={termsModalOpen}
                onClose={() => setTermsModalOpen(false)}
                onAccept={() => setTermsModalOpen(false)}
                onShowPrivacy={() => { setTermsModalOpen(false); setPrivacyModalOpen(true); }}
                viewOnly
            />
            <PrivacyModal
                open={privacyModalOpen}
                onClose={() => setPrivacyModalOpen(false)}
            />
            <ProPlusBillingTermsModal
                open={proPlusTermsModalOpen}
                onClose={() => setProPlusTermsModalOpen(false)}
            />
            {/* iter87.180.14 — Compact Login modal reachable from the
                top-ribbon Access Your Rivalry dropdown. Reuses the
                same three-field form as the hero card so we don't
                fork the join API contract.
                iter87.193.6 — Restored the inline recover form. The
                "Forgot username?" button used to close the modal AND
                flip `mode="recover"`, but the recover-mode branch was
                dropped in an earlier cleanup — so the button appeared
                to do nothing. The button now swaps the modal body
                between sign-in and recover-request without closing. */}
            {loginModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
                    data-testid="login-modal-backdrop"
                    onClick={(e) => { if (e.target === e.currentTarget) { setLoginModalOpen(false); setMode("join"); setRecoverSent(false); } }}
                >
                    <div
                        className="brutal-card max-w-sm w-full p-5 space-y-4"
                        data-testid="login-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <LogIn className="w-5 h-5 text-cyber shrink-0" />
                                <h2 className="font-display font-black text-lg uppercase truncate">
                                    {mode === "recover" ? "Recover access" : "Sign back in"}
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setLoginModalOpen(false); setMode("join"); setRecoverSent(false); }}
                                className="brutal-btn-ghost p-1.5"
                                aria-label="Close"
                                data-testid="login-modal-close-btn"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        {mode === "recover" ? (
                            recoverSent ? (
                                <div className="space-y-3" data-testid="login-modal-recover-sent">
                                    <p className="text-xs text-zinc-400 leading-relaxed">
                                        Check your inbox — if the invite code and email match a player on file, we've sent a magic-link recovery email. Click the link on the same device you want to sign back in from.
                                    </p>
                                    <p className="text-[11px] text-zinc-500 leading-relaxed italic">
                                        Nothing arriving after a few minutes? Ask your rivalry admin to re-send from the Players tab, or double-check the email you gave them when you joined.
                                    </p>
                                    <div className="pt-1 flex gap-2 justify-end">
                                        <button
                                            type="button"
                                            onClick={() => { setMode("join"); setRecoverSent(false); setRecoverCode(""); setRecoverEmail(""); }}
                                            className="brutal-btn-ghost text-xs py-2"
                                            data-testid="login-modal-back-to-signin-btn"
                                        >
                                            Back to sign in
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-xs text-zinc-400 leading-relaxed">
                                        Lost your personal code or username? Enter your invite code and the email you gave your rivalry admin. We'll send you a magic-link email that signs you back in instantly.
                                    </p>
                                    <form onSubmit={handleRecover} className="space-y-3" data-testid="login-modal-recover-form">
                                        <div>
                                            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Invite code</label>
                                            <input
                                                type="text"
                                                value={recoverCode}
                                                onChange={(e) => setRecoverCode(e.target.value.toUpperCase())}
                                                placeholder="ABC123"
                                                className="brutal-input font-mono uppercase text-sm py-2 w-full"
                                                data-testid="login-modal-recover-invite-input"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Email on file</label>
                                            <input
                                                type="email"
                                                value={recoverEmail}
                                                onChange={(e) => setRecoverEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                className="brutal-input text-sm py-2 w-full"
                                                data-testid="login-modal-recover-email-input"
                                                required
                                            />
                                        </div>
                                        <div className="pt-1 flex gap-2 justify-end">
                                            <button
                                                type="button"
                                                onClick={() => setMode("join")}
                                                className="brutal-btn-ghost text-xs py-2"
                                                data-testid="login-modal-recover-back-btn"
                                            >
                                                Back
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                data-testid="login-modal-recover-submit-btn"
                                                className="brutal-btn text-xs py-2 bg-cyber text-black border-black shadow-brutal"
                                            >
                                                {loading ? "Sending…" : "Send recovery email"}
                                            </button>
                                        </div>
                                    </form>
                                    <p className="text-[10px] text-zinc-500 italic pt-1">
                                        No email on file? Ask your rivalry admin to add one via Players → Edit, or to issue a fresh personal code you can use directly.
                                    </p>
                                </>
                            )
                        ) : (
                            <>
                        <p className="text-xs text-zinc-400 leading-relaxed">
                            Enter the three details you were given when your Rivalry admin invited you. Works for crewmates and league admins.
                        </p>
                        <form onSubmit={(e) => { e.preventDefault(); setLoginModalOpen(false); handleJoin(e); }} className="space-y-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Invite code</label>
                                <input
                                    type="text"
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                    placeholder="ABC123"
                                    className="brutal-input font-mono uppercase text-sm py-2 w-full"
                                    data-testid="login-modal-invite-input"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Personal code</label>
                                <input
                                    type="text"
                                    value={joinPersonalCode}
                                    onChange={(e) => setJoinPersonalCode(e.target.value.toUpperCase())}
                                    placeholder="XYZ-1234"
                                    className="brutal-input font-mono uppercase text-sm py-2 w-full"
                                    data-testid="login-modal-pcode-input"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Username</label>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="the-name-you-picked"
                                    className="brutal-input text-sm py-2 w-full"
                                    data-testid="login-modal-username-input"
                                    required
                                />
                            </div>
                            <div className="pt-1 flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setMode("recover")}
                                    className="brutal-btn-ghost text-xs py-2"
                                    data-testid="login-modal-forgot-btn"
                                >
                                    Forgot username?
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="login-modal-submit-btn"
                                    className="brutal-btn text-xs py-2 bg-cyber text-black border-black shadow-brutal"
                                >
                                    {loading ? "Signing in…" : "Sign in"}
                                </button>
                            </div>
                        </form>
                        <p className="text-[10px] text-zinc-500 italic pt-1">
                            Personal codes and invite codes don't expire. Save your personal code somewhere safe!
                        </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
